import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Upload, FileText, CheckCircle2 } from "lucide-react";
import { PHONE_CODES, STATUS_IN_CANADA, PROGRAMS } from "../../lib/data";
import { toast } from "sonner";

const Field = ({ label, required, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-sm font-medium text-slate-700">
      {label} {required && <span className="text-[#C49A3A]">*</span>}
    </span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className="w-full rounded-md border border-slate-300 bg-white px-3.5 py-2.5 text-sm input-focus"
  />
);

const Select = ({ children, ...props }) => (
  <select
    {...props}
    className="w-full rounded-md border border-slate-300 bg-white px-3.5 py-2.5 text-sm input-focus"
  >
    {children}
  </select>
);

function FileBox({ label, file, onFile, hint }) {
  return (
    <div className="border border-dashed border-slate-300 rounded-xl p-4 bg-slate-50/60">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-slate-800">{label}</div>
          {hint && <div className="text-xs text-slate-500">{hint}</div>}
        </div>
        <label className="inline-flex items-center gap-2 bg-white border border-slate-300 px-3.5 py-1.5 rounded-md text-xs font-medium text-slate-700 hover:border-cct-blue hover:text-cct-blue cursor-pointer">
          <Upload className="w-3.5 h-3.5" /> Upload
          <input
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => onFile(e.target.files?.[0] || null)}
          />
        </label>
      </div>
      {file && (
        <div className="mt-2 flex items-center gap-2 text-sm bg-white border border-slate-200 rounded-md px-3 py-2">
          <FileText className="w-4 h-4 text-cct-blue" />
          <span className="truncate text-slate-700">{file.name}</span>
          <button
            onClick={() => onFile(null)}
            className="ml-auto text-xs text-slate-500 hover:text-red-500"
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}

export default function RecruiterNew({ onSubmit, onDone }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    phone_code: "+1",
    date_of_birth: "",
    nationality: "",
    status_in_canada: "",
    program: "",
  });
  const [idDoc, setIdDoc] = useState(null);
  const [supDoc, setSupDoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    const req = [
      "full_name",
      "email",
      "phone",
      "date_of_birth",
      "nationality",
      "status_in_canada",
    ];
    for (const k of req)
      if (!form[k]) return toast.error(`Missing: ${k.replace(/_/g, " ")}`);
    setSubmitting(true);
    try {
      await onSubmit(form, idDoc, supDoc);
      setSuccess(true);
      setTimeout(() => onDone(), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (success)
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white border border-slate-200 rounded-2xl shadow-sm p-12 text-center"
      >
        <div className="w-16 h-16 rounded-full bg-emerald-50 mx-auto flex items-center justify-center">
          <CheckCircle2 className="w-9 h-9 text-emerald-600" />
        </div>
        <h2 className="mt-4 font-display text-2xl font-semibold text-cct-blue">
          Referral Submitted
        </h2>
        <p className="mt-1 text-slate-500 text-sm">Redirecting...</p>
      </motion.div>
    );

  return (
    <div className="max-w-3xl">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8">
        <h2 className="font-display text-2xl font-semibold text-cct-blue">
          Submit a Student Referral
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Fill out the student’s details. The application will be tagged to
          your partner account automatically.
        </p>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Full Name" required className="sm:col-span-2">
            <Input
              value={form.full_name}
              onChange={(e) => update("full_name", e.target.value)}
            />
          </Field>
          <Field label="Email" required>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
            />
          </Field>
          <Field label="Phone" required>
            <div className="flex gap-2">
              <Select
                value={form.phone_code}
                onChange={(e) => update("phone_code", e.target.value)}
              >
                {PHONE_CODES.map((p) => (
                  <option key={p.region} value={p.code}>
                    {p.region} {p.code}
                  </option>
                ))}
              </Select>
              <Input
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
              />
            </div>
          </Field>
          <Field label="Date of Birth" required>
            <Input
              type="date"
              value={form.date_of_birth}
              onChange={(e) => update("date_of_birth", e.target.value)}
            />
          </Field>
          <Field label="Nationality" required>
            <Input
              value={form.nationality}
              onChange={(e) => update("nationality", e.target.value)}
            />
          </Field>
          <Field label="Status in Canada" required className="sm:col-span-2">
            <Select
              value={form.status_in_canada}
              onChange={(e) => update("status_in_canada", e.target.value)}
            >
              <option value="">Select status</option>
              {STATUS_IN_CANADA.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </Select>
          </Field>
          <Field label="Program of Interest" className="sm:col-span-2">
            <Select
              value={form.program}
              onChange={(e) => update("program", e.target.value)}
            >
              <option value="">— Select —</option>
              {PROGRAMS.map((p) => (
                <option
                  key={`${p.name}-${p.type}`}
                  value={`${p.name} — ${p.type}`}
                >
                  {p.name} — {p.type}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FileBox
            label="ID Document"
            hint="Passport / driving license / ID"
            file={idDoc}
            onFile={setIdDoc}
          />
          <FileBox
            label="Supporting Document"
            hint="Resume, transcripts, etc."
            file={supDoc}
            onFile={setSupDoc}
          />
        </div>

        <div className="mt-6 flex justify-end">
          <button
            disabled={submitting}
            onClick={submit}
            className="inline-flex items-center gap-2 bg-cct-blue text-white text-sm font-semibold px-5 py-2.5 rounded-md hover:bg-[#1a3380] disabled:opacity-60"
          >
            {submitting ? "Submitting..." : "Submit Referral"}
            {!submitting && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
